import { useState } from 'react'
import { XStack, YStack } from 'tamagui'
import { useToastController } from '@tamagui/toast'
import { Input } from '@components/Input'
import { Button } from '@components/Button'
import { SectionCard } from '@components/Profile/SectionCard'
import { useComponentTranslation } from '@hooks/useComponentTranslation'
import { useUserUpdate } from '@services/api/graphql/mutations/userUpdate'
import { ICustomField } from '@dtos/User'

interface ICustomFieldsSectionProps {
  fields: ICustomField[]
}

export function CustomFieldsSection({ fields }: ICustomFieldsSectionProps) {
  const { t } = useComponentTranslation('Profile')
  const toast = useToastController()
  const { handleUpdateUser, loading } = useUserUpdate()

  const [values, setValues] = useState<Record<string, string>>(() =>
    fields.reduce<Record<string, string>>((acc, field) => {
      acc[field.slug] = field.value ?? ''
      return acc
    }, {}),
  )

  function handleChange(slug: string, value: string) {
    setValues((prev) => ({ ...prev, [slug]: value }))
  }

  async function handleSave() {
    const customFields = Object.entries(values).map(([slug, value]) => ({
      slug,
      value,
    }))

    const result = await handleUpdateUser({ customFields })
    if (result?.errors?.length) {
      toast.show(t('toast.errorTitle'), {
        message: result.errors.join('\n'),
        type: 'error',
      })
      return
    }

    toast.show(t('toast.successTitle'), { message: t('toast.successMessage') })
  }

  if (!fields.length) return null

  return (
    <SectionCard title={t('customFields.title')}>
      <YStack gap="$size.spacing-xs">
        {fields.map((field) => (
          <Input
            key={field.slug}
            label={field.label ?? field.slug}
            value={values[field.slug]}
            onChangeText={(text) => handleChange(field.slug, text)}
          />
        ))}

        <XStack justifyContent="flex-end">
          <Button variant="primary" loading={loading} onPress={handleSave}>
            {t('actions.save')}
          </Button>
        </XStack>
      </YStack>
    </SectionCard>
  )
}
